import type { RefObject } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { useDebounceCallback } from '@elfsight/widget-ui';
import type { DropdownOption } from '../text-dropdown';
import { scrollToDropdownItem } from '../utils';

export interface UseDropdownScrollOptions {
  contentRef: RefObject<HTMLDivElement>;
  open: boolean;
  localValue: string;
  options: DropdownOption[];
  nearestOption?: DropdownOption | null;
  disableAutoScroll?: boolean;
  highlightedIndex?: number;
}

export function useDropdownScroll({
  contentRef,
  open,
  localValue,
  options,
  nearestOption,
  disableAutoScroll = false,
  highlightedIndex = -1
}: UseDropdownScrollOptions) {
  const previousOpenRef = useRef(false);
  const justOpenedRef = useRef(false);

  const scrollToHighlightedItem = useCallback(
    (container: HTMLDivElement, behavior: ScrollBehavior) => {
      const items = Array.from(container.children) as HTMLElement[];
      const highlightedElement = items[highlightedIndex];

      if (!highlightedElement) {
        return;
      }

      const containerHeight = container.clientHeight;
      const itemTop = highlightedElement.offsetTop;
      const itemHeight = highlightedElement.offsetHeight;
      const scrollPosition = itemTop - containerHeight / 2 + itemHeight / 2;

      if (behavior === 'auto') {
        container.scrollTop = Math.max(0, scrollPosition);
        return;
      }

      container.scrollTo({
        top: Math.max(0, scrollPosition),
        behavior
      });
    },
    [highlightedIndex]
  );

  const findTargetOption = useCallback(() => {
    const optionByValue = options.find((opt) => opt.value === localValue);
    if (optionByValue) {
      return optionByValue.value;
    }

    const optionByLabel = options.find((opt) => opt.label === localValue);
    if (optionByLabel) {
      return optionByLabel.value;
    }

    if (nearestOption && !disableAutoScroll) {
      return nearestOption.value;
    }

    return localValue;
  }, [options, localValue, nearestOption, disableAutoScroll]);

  const performScroll = useCallback(
    (behavior: ScrollBehavior) => {
      if (!contentRef.current) {
        return;
      }

      if (highlightedIndex >= 0 && highlightedIndex < options.length) {
        scrollToHighlightedItem(contentRef.current, behavior);
        return;
      }

      const targetValue = findTargetOption();
      scrollToDropdownItem({
        dropdownRef: contentRef,
        options,
        targetValue,
        behavior
      });
    },
    [
      contentRef,
      options,
      highlightedIndex,
      scrollToHighlightedItem,
      findTargetOption
    ]
  );

  const performScrollRef = useRef(performScroll);
  useEffect(() => {
    performScrollRef.current = performScroll;
  }, [performScroll]);

  const scrollSmooth = useCallback(() => {
    performScrollRef.current('smooth');
  }, []);

  const debouncedScroll = useDebounceCallback(scrollSmooth, 200);

  useEffect(() => {
    const justOpened = open && !previousOpenRef.current;
    previousOpenRef.current = open;

    if (!justOpened) {
      return;
    }

    justOpenedRef.current = true;

    const rafIds = { raf2: 0 };
    const raf1Id = requestAnimationFrame(() => {
      rafIds.raf2 = requestAnimationFrame(() => {
        performScrollRef.current('auto');
        setTimeout(() => {
          justOpenedRef.current = false;
        }, 100);
      });
    });

    return () => {
      cancelAnimationFrame(raf1Id);
      cancelAnimationFrame(rafIds.raf2);
    };
  }, [open, performScroll]);

  useEffect(() => {
    if (highlightedIndex < 0 || !open) {
      return;
    }

    debouncedScroll();
  }, [highlightedIndex, open, debouncedScroll]);

  useEffect(() => {
    if (justOpenedRef.current) {
      return;
    }

    if (highlightedIndex >= 0) {
      return;
    }

    if (open && previousOpenRef.current) {
      debouncedScroll();
    }
  }, [localValue, open, debouncedScroll, highlightedIndex]);
}
